import type { Bitset } from '~/utils/filter/bitset'
import { andInto, countAnd, createBitset, orInto, setBit } from '~/utils/filter/bitset'

export interface FacetIndexConfig<Item> {
  docKeys: (doc: Item) => string[]
  optionKey: (value: string) => string
}

type FacetOption = { value: string, label: string }
type CountWithOption = (option: FacetOption) => number

// The key must also point to an array field. Otherwise non-array fields could be configured as facets and silently ignored.
export type FacetKeys<State> = { [K in keyof State]: State[K] extends FacetOption[] ? K : never }[keyof State] & string

export function useFacetIndex<Item, State extends object, Key extends FacetKeys<State>>(source: MaybeRefOrGetter<Item[]>, committed: ComputedRef<State>, matches: (doc: Item, state: State) => boolean, configs: Record<Key, FacetIndexConfig<Item>>): Record<Key, CountWithOption> {
  const facetKeys = Object.keys(configs) as Key[]

  // All consumers read `source` through this, keeping the bitsets and combine width on the same generation
  const dataset = computed(() => toValue(source))

  const baseState = computed(() => {
    const state = { ...committed.value } as Record<string, unknown>
    for (const key of facetKeys) {
      state[key] = []
    }
    return state as State
  })

  const baseBits = computed(() => {
    const docs = dataset.value
    const state = baseState.value
    const bits = createBitset(docs.length)
    for (let i = 0; i < docs.length; i++) {
      if (matches(docs[i]!, state)) {
        setBit(bits, i)
      }
    }
    return bits
  })

  const indexes = computed(() => {
    const docs = dataset.value
    const built = {} as Record<Key, Map<string, Bitset>>
    for (const key of facetKeys) {
      built[key] = new Map<string, Bitset>()
    }

    for (let i = 0; i < docs.length; i++) {
      const doc = docs[i]!
      for (const key of facetKeys) {
        const map = built[key]!
        for (const docKey of configs[key].docKeys(doc)) {
          let bits = map.get(docKey)
          if (!bits) {
            bits = createBitset(docs.length)
            map.set(docKey, bits)
          }
          setBit(bits, i)
        }
      }
    }
    return built
  })

  function bitsFor(key: Key, value: string): Bitset | undefined {
    return indexes.value[key]!.get(configs[key].optionKey(value))
  }

  function combine(key: Key, values: string[], size: number): Bitset {
    const acc = createBitset(size)

    for (const value of values) {
      const bits = bitsFor(key, value)
      if (bits) {
        orInto(acc, bits)
      }
    }
    return acc
  }

  const selections = {} as Record<Key, ComputedRef<Bitset | null>>
  for (const key of facetKeys) {
    selections[key] = computed(() => {
      const selected = committed.value[key] as FacetOption[] | undefined
      if (!selected?.length) {
        return null
      }
      return combine(key, selected.map(v => v.value), dataset.value.length)
    })
  }

  const facetBases = {} as Record<Key, ComputedRef<Bitset>>
  for (const key of facetKeys) {
    facetBases[key] = computed(() => {
      // We use `slice()` to keep the copy in sync with `baseBits` width.
      const bits = baseBits.value.slice()

      for (const other of facetKeys) {
        if (other === key) {
          continue
        }
        const selected = selections[other]!.value
        if (selected) {
          andInto(bits, selected)
        }
      }
      return bits
    })
  }

  const counters = {} as Record<Key, CountWithOption>
  for (const key of facetKeys) {
    counters[key] = (option: FacetOption) => {
      const base = facetBases[key]!.value
      const bits = bitsFor(key, option.value)
      return bits ? countAnd(base, bits) : 0
    }
  }

  return counters
}
